/**
 * Mapping between zInspector API records and the record shapes Atomic CRM's screens use.
 * Field meanings: zcrm-docs/discovery-handoff.md ("Exact fields for the mapping") and crmConfig.ts.
 */
import type { Identifier } from "ra-core";

import type { Company, Contact, Deal, Sale } from "../../types";
import type { ZiUser } from "./client";
import {
  DEAL_TYPES,
  PRODUCTS,
  parseAccountGroup,
  stageByStatus,
  statusByStage,
  STAGE_SINCE_TASK_FIELD,
  STAGES,
} from "./crmConfig";

export interface ZiProperty {
  id: number;
  Name: string;
  Address?: string | null;
  UserGroup?: string | null;
  AccountingID?: string | null;
  IsArchived?: boolean;
  LastActivity?: string | null;
  LastUpdate?: string | null;
}

export interface ZiContactProperty {
  id: number;
  Name?: string;
  lease_status?: string | null;
}

export interface ZiContact {
  id: number;
  Type?: string;
  FirstName?: string | null;
  LastName?: string | null;
  Company?: string | null;
  Email?: string | null;
  OfficePhone?: string | null;
  MobilePhone?: string | null;
  Comments?: string | null;
  TaxId?: string | null;
  Is1099Required?: boolean;
  IsPrimary?: boolean;
  Properties?: ZiContactProperty[];
}

export interface ZiTask {
  id: number;
  Action?: string;
  Status?: string;
  Priority?: number;
  Property?: number | null;
  PropertyName?: string;
  Description?: string | null;
  ManagedBy?: number | null;
  AssignedToContact?: number | null;
  ReferenceNumber?: string | null;
  ExternalReferenceNumber?: string | null;
  StartDate?: string | null;
  EndDate?: string | null;
  EntranceInstruction?: string | null;
  Key?: string | null;
  KeyDesc?: string | null;
  AuthorizedLimit?: string | null;
  WorkOrderInstructions?: string | null;
  Notes?: string | null;
  Pets?: boolean;
  PetsDesc?: string | null;
  TenantNames?: string | null;
  PhoneNumber?: string | null;
  Email?: string | null;
  DateCreated?: string | null;
  LastModified?: string | null;
  NextToDoName?: string | null;
}

export interface ZiLease {
  id: number;
  Property: number;
  PropertyName?: string;
  Status?: string;
  Type?: string;
  SigStatus?: string;
  IsInactive?: boolean;
  LeaseStart?: string | null;
  LeaseEnd?: string | null;
  MoveIn?: string | null;
  MoveOut?: string | null;
  Rent?: number | string | null;
  Comments?: string | null;
  PrimaryTenant?: ZiContact | null;
  Tenants?: ZiContact[];
  LastUpdate?: string | null;
}

/** A deal as the CRM sees it: Atomic CRM's Deal plus the zCRM-only fields. */
export type ZcrmDeal = Omit<Deal, "archived_at"> & {
  product: string | null;
  loss_reason: string;
  reference: string;
  early_contact_name: string;
  early_contact_phone: string;
  early_contact_email: string;
  booked_at_booth: boolean;
  /** When the deal entered its current stage (see STAGE_SINCE_TASK_FIELD). */
  stage_since: string;
  /** For a won, lost or snoozed deal: the stage it was in when it closed ("" if unknown). */
  closed_from: string;
  company_name: string;
  archived_at: null;
};

/** An account (zInspector property) in Atomic CRM's company shape. */
export type ZcrmCompany = Partial<Company> &
  Pick<Company, "id" | "name"> & {
    market: string | null;
    user_group: string;
    accounting_id: string;
    is_archived: boolean;
    last_activity: string | null;
  };

const text = (value: string | null | undefined): string => value ?? "";

/**
 * Splits the property list's one-line address ("1450 Larimer St, Suite 300, Denver, CO 80202")
 * into parts. Anything that doesn't end in "<City>, <ST> <ZIP>" stays whole in `address`.
 */
export const parseAddress = (
  oneLine: string | null | undefined,
): { address: string; city: string; state_abbr: string; zipcode: string } => {
  const parts = text(oneLine)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const stateZip = parts.at(-1)?.match(/^([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (!stateZip || parts.length < 3) {
    return { address: text(oneLine), city: "", state_abbr: "", zipcode: "" };
  }
  return {
    address: parts.slice(0, -2).join(", "),
    city: parts.at(-2) ?? "",
    state_abbr: stateZip[1],
    zipcode: stateZip[2],
  };
};

export const isAccountProperty = (property: ZiProperty): boolean =>
  parseAccountGroup(text(property.UserGroup)).isAccount;

export const propertyToCompany = (property: ZiProperty): ZcrmCompany => ({
  id: property.id,
  name: property.Name,
  ...parseAddress(property.Address),
  country: "",
  market: parseAccountGroup(text(property.UserGroup)).market,
  user_group: text(property.UserGroup),
  accounting_id: text(property.AccountingID),
  is_archived: property.IsArchived ?? false,
  last_activity: property.LastActivity ?? null,
  created_at: property.LastUpdate ?? "",
  website: "",
  phone_number: "",
  description: "",
  context_links: [],
});

export interface ZiZDataRow {
  area_name: string;
  detail: string;
  value: string;
}

/** A Support task, as an account page lists it. */
export type ZcrmTicket = {
  id: number;
  subject: string;
  status: string;
  is_open: boolean;
  reference: string;
  company_id: number | null;
  sales_id: number | null;
  created_at: string;
  updated_at: string;
};

const CLOSED_TASK_STATUSES = new Set(["Closed", "Canceled"]);

export const taskToTicket = (task: ZiTask): ZcrmTicket => ({
  id: task.id,
  subject: text(task.Description),
  status: text(task.Status),
  is_open: !CLOSED_TASK_STATUSES.has(text(task.Status)),
  reference: text(task.ReferenceNumber),
  company_id: task.Property ?? null,
  sales_id: task.ManagedBy ?? null,
  created_at: text(task.DateCreated),
  updated_at: text(task.LastModified),
});

export const contactToCrm = (
  contact: ZiContact,
  accountIds?: ReadonlySet<Identifier>,
): Contact & { role: string; is_primary: boolean } => {
  const properties = contact.Properties ?? [];
  const account =
    properties.find((p) => !accountIds || accountIds.has(p.id)) ??
    properties[0];
  return {
    id: contact.id,
    first_name: text(contact.FirstName),
    last_name: text(contact.LastName),
    title: text(contact.TaxId),
    role: text(contact.TaxId),
    is_primary: contact.IsPrimary ?? false,
    company_id: account?.id ?? null,
    company_name: text(account?.Name ?? contact.Company),
    email_jsonb: contact.Email ? [{ email: contact.Email, type: "Work" }] : [],
    phone_jsonb: [
      ...(contact.OfficePhone
        ? [{ number: contact.OfficePhone, type: "Work" as const }]
        : []),
      ...(contact.MobilePhone
        ? [{ number: contact.MobilePhone, type: "Other" as const }]
        : []),
    ],
    has_newsletter: contact.Is1099Required ?? false,
    background: text(contact.Comments),
    first_seen: "",
    last_seen: "",
    status: "",
    gender: "",
    tags: [],
  };
};

/** AuthorizedLimit is free text ("45", "$1,200"); the CRM shows a number. */
export const parseAmount = (value: string | null | undefined): number => {
  const amount = Number.parseFloat(text(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const byStored = <T extends { id: string; stored: string }>(
  list: readonly T[],
  stored: string | null | undefined,
): string | null => list.find((item) => item.stored === stored)?.id ?? null;

const storedById = <T extends { id: string; stored: string }>(
  list: readonly T[],
  id: string | null | undefined,
): string => list.find((item) => item.id === id)?.stored ?? "";

/** Sales tasks whose status is outside the stage map (Work Complete, Invoice Received) never reach the board. */
export const isBoardStatus = (status: string | undefined): boolean =>
  !!status && !!stageByStatus(status);

/**
 * zCRM's stage mark on a deal (STAGE_SINCE_TASK_FIELD): "<stage id>|<ISO date>", written on
 * every stage move. For an open deal it's the stage it entered and when; for a closed one, the
 * stage it closed from and when, which is how the funnel knows where deals are lost.
 */
export const parseStageMark = (
  mark: string | null | undefined,
): { stage: string; at: string } => {
  const [stage, at] = text(mark).includes("|")
    ? text(mark).split("|")
    : ["", text(mark)];
  return { stage, at };
};

const isEndStage = (id: string) =>
  STAGES.some((s) => s.id === id && "end" in s && s.end);

const stageMarkFields = (
  task: ZiTask,
): Pick<ZcrmDeal, "stage_since" | "closed_from"> => {
  const stage = stageByStatus(text(task.Status))?.id ?? "";
  const mark = parseStageMark(task[STAGE_SINCE_TASK_FIELD]);
  const lastChange = text(task.LastModified ?? task.DateCreated);
  if (isEndStage(stage)) {
    return {
      stage_since: mark.at || lastChange,
      closed_from: isEndStage(mark.stage) ? "" : mark.stage,
    };
  }
  // A mark for another stage means the deal was moved in the dashboard since.
  const markIsCurrent = !mark.stage || mark.stage === stage;
  return {
    stage_since: (markIsCurrent && mark.at) || lastChange,
    closed_from: "",
  };
};

export const taskToDeal = (task: ZiTask): ZcrmDeal => ({
  id: task.id,
  name: text(task.Description),
  company_id: task.Property ?? "",
  company_name: text(task.PropertyName),
  contact_ids: task.AssignedToContact ? [task.AssignedToContact] : [],
  category: byStored(DEAL_TYPES, task.EntranceInstruction) ?? "",
  stage: stageByStatus(text(task.Status))?.id ?? "",
  description: text(task.WorkOrderInstructions),
  amount: parseAmount(task.AuthorizedLimit),
  created_at: text(task.DateCreated),
  updated_at: text(task.LastModified ?? task.DateCreated),
  expected_closing_date: text(task.EndDate),
  sales_id: task.ManagedBy ?? "",
  index: 0,
  archived_at: null,
  product: byStored(PRODUCTS, task.Key),
  loss_reason: text(task.KeyDesc),
  reference: text(task.ExternalReferenceNumber),
  early_contact_name: text(task.TenantNames),
  early_contact_phone: text(task.PhoneNumber),
  early_contact_email: text(task.Email),
  booked_at_booth: task.Pets ?? false,
  ...stageMarkFields(task),
});

type TaskPatch = Record<string, string | number | boolean | null>;

/** Each CRM deal field and how it is written back to the task (PATCH /api/task/edit/{id}/). */
const DEAL_FIELD_WRITERS: Record<
  string,
  (deal: Partial<ZcrmDeal>) => TaskPatch
> = {
  name: (deal) => ({ Description: text(deal.name) }),
  stage: (deal) => ({ Status: statusByStage(text(deal.stage)) ?? null }),
  category: (deal) => ({
    EntranceInstruction: storedById(DEAL_TYPES, deal.category),
  }),
  product: (deal) => ({ Key: storedById(PRODUCTS, deal.product) }),
  amount: (deal) => ({
    AuthorizedLimit: deal.amount == null ? "" : String(deal.amount),
  }),
  description: (deal) => ({ WorkOrderInstructions: text(deal.description) }),
  loss_reason: (deal) => ({ KeyDesc: text(deal.loss_reason) }),
  sales_id: (deal) => ({
    ManagedBy: deal.sales_id ? Number(deal.sales_id) : null,
  }),
  company_id: (deal) => ({
    Property: deal.company_id ? Number(deal.company_id) : null,
  }),
  contact_ids: (deal) => ({
    AssignedToContact: deal.contact_ids?.length
      ? Number(deal.contact_ids[0])
      : null,
  }),
  expected_closing_date: (deal) => ({
    EndDate: deal.expected_closing_date || null,
  }),
  early_contact_name: (deal) => ({
    TenantNames: text(deal.early_contact_name),
  }),
  early_contact_phone: (deal) => ({
    PhoneNumber: text(deal.early_contact_phone),
  }),
  early_contact_email: (deal) => ({ Email: text(deal.early_contact_email) }),
  booked_at_booth: (deal) => ({ Pets: !!deal.booked_at_booth }),
};

const sameValue = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * The task fields to PATCH for a deal edit: only the fields that changed and have a
 * zInspector home. Board-only changes (the card `index`) produce an empty patch.
 */
export const dealToTaskPatch = (
  data: Partial<ZcrmDeal>,
  previous: Partial<ZcrmDeal> = {},
): TaskPatch => {
  const patch: TaskPatch = {};
  for (const [field, write] of Object.entries(DEAL_FIELD_WRITERS)) {
    if (!(field in data)) continue;
    const key = field as keyof ZcrmDeal;
    if (sameValue(data[key], previous[key])) continue;
    Object.assign(patch, write(data));
  }
  if ("Status" in patch && patch.Status === null) {
    throw new Error(`Unknown deal stage "${data.stage}"`);
  }
  if ("Status" in patch) {
    // zInspector keeps no stage history: mark the stage entered and when, or, when the deal
    // closes, the stage it closed from (see parseStageMark).
    const to = text(data.stage);
    const from = text(previous.stage);
    const markStage = isEndStage(to) ? (isEndStage(from) ? "" : from) : to;
    patch[STAGE_SINCE_TASK_FIELD] = `${markStage}|${new Date().toISOString()}`;
  }
  return patch;
};

export const userToSale = (user: ZiUser): Sale => ({
  id: user.id,
  user_id: String(user.id),
  first_name: text(user.FirstName),
  last_name: text(user.LastName),
  email: text(user.email),
  administrator: user.role === "Admin",
  disabled: false,
});

export const leaseToEngagement = (lease: ZiLease) => ({
  id: lease.id,
  company_id: lease.Property,
  company_name: text(lease.PropertyName),
  status: text(lease.Status),
  type: text(lease.Type),
  signature_status: text(lease.SigStatus),
  is_inactive: lease.IsInactive ?? false,
  start_date: lease.LeaseStart ?? null,
  end_date: lease.LeaseEnd ?? null,
  move_in: lease.MoveIn ?? null,
  move_out: lease.MoveOut ?? null,
  rent: lease.Rent == null ? null : Number(lease.Rent),
  comments: text(lease.Comments),
  main_contact_id: lease.PrimaryTenant?.id ?? null,
  main_contact_name: lease.PrimaryTenant
    ? `${text(lease.PrimaryTenant.FirstName)} ${text(lease.PrimaryTenant.LastName)}`.trim()
    : "",
  participant_ids: (lease.Tenants ?? []).map((tenant) => tenant.id),
  updated_at: text(lease.LastUpdate),
});
